import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Apple,
  ArrowRight,
  Bell,
  CalendarDays,
  ClipboardPlus,
  Droplets,
  History,
  Plus,
  Quote,
  Scale,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Utensils,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { listFoodLogs } from "../lib/v2Database";
import {
  buildNutrientCards,
  buildPeriodStats,
  buildSafetySummary,
  filterLogsByDate,
  filterLogsForActiveSession,
  getLocalDateKey,
  sumNutrients,
  V2_DASHBOARD_STATUS,
} from "../lib/v2DashboardCalculations";
import {
  getV2DashboardQuote,
  getV2DashboardTips,
  loadV2AdminAppSettings,
  loadV2FoodLogs,
} from "../lib/v2Storage";

const statusStyles = {
  [V2_DASHBOARD_STATUS.safe]: {
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
    bar: "bg-emerald-500",
    icon: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200",
  },
  [V2_DASHBOARD_STATUS.watch]: {
    badge: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200",
    bar: "bg-amber-500",
    icon: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200",
  },
  [V2_DASHBOARD_STATUS.over]: {
    badge: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200",
    bar: "bg-rose-500",
    icon: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-200",
  },
  [V2_DASHBOARD_STATUS.notConfigured]: {
    badge: "border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200",
    bar: "bg-slate-400",
    icon: "bg-slate-100 text-slate-700 dark:bg-slate-950 dark:text-slate-200",
  },
};

const nutrientIcons = {
  potassium: Apple,
  phosphorus: Sparkles,
  sodium: Droplets,
  protein: Utensils,
};

export default function V2Dashboard() {
  const { lang } = useLang();
  const { isGuest, isRegistered, loading, settings, session, user } = useV2Session();
  const copy = getV2Copy(lang);
  const dashboardCopy = copy.dashboardCopy;
  const userId = session?.user?.id || "";

  const [dashboardData, setDashboardData] = useState({ foodLogs: [], error: "" });

  useEffect(() => {
    let alive = true;

    async function loadDashboardLogs() {
      try {
        if (isRegistered && user?.id) {
          const loadedLogs = await listFoodLogs(user.id);
          if (alive) setDashboardData({ foodLogs: loadedLogs, error: "" });
          return;
        }

        const loadedLogs = loadV2FoodLogs({ isGuest });
        const scopedLogs = filterLogsForActiveSession(loadedLogs, { isGuest, userId });
        if (alive) setDashboardData({ foodLogs: scopedLogs, error: "" });
      } catch {
        if (alive) setDashboardData({ foodLogs: [], error: dashboardCopy.dataErrorBody });
      }
    }

    loadDashboardLogs();
    return () => {
      alive = false;
    };
  }, [dashboardCopy.dataErrorBody, isGuest, isRegistered, user?.id, userId]);

  const foodLogs = dashboardData.foodLogs;
  const todayKey = getLocalDateKey();
  const todayLogs = useMemo(() => filterLogsByDate(foodLogs, todayKey), [foodLogs, todayKey]);
  const todayTotals = useMemo(() => sumNutrients(todayLogs), [todayLogs]);
  const nutrientCards = useMemo(
    () => buildNutrientCards(todayTotals, settings.daily_targets, dashboardCopy),
    [dashboardCopy, settings.daily_targets, todayTotals]
  );
  const safetySummary = useMemo(
    () => buildSafetySummary(nutrientCards, { hasLogs: todayLogs.length > 0, copy: dashboardCopy }),
    [dashboardCopy, nutrientCards, todayLogs.length]
  );
  const periodStats = useMemo(
    () => buildPeriodStats(foodLogs, settings.daily_targets, new Date(), dashboardCopy),
    [dashboardCopy, foodLogs, settings.daily_targets]
  );
  const quote = useMemo(() => getV2DashboardQuote(), []);
  const tips = useMemo(() => getV2DashboardTips(), []);
  const adminAppSettings = useMemo(() => loadV2AdminAppSettings(), []);
  const showWeightFluidCard = Boolean(
    adminAppSettings.show_weight_fluid_card ||
      settings.daily_targets?.show_weight_fluid_card ||
      settings.visibility?.weight_fluid_card
  );
  const displayName = session?.user?.full_name || settings.profile?.full_name || (isGuest ? copy.guest : copy.profileSession);
  const formattedToday = formatDateForDisplay(todayKey, lang);

  if (loading) {
    return (
      <section className="mx-auto max-w-7xl space-y-6" aria-busy="true">
        <div className="rounded-lg border border-border bg-card p-5 text-sm font-semibold text-muted-foreground">
          {dashboardCopy.loading}
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl min-w-0 space-y-6 overflow-x-hidden">
      <header className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="min-w-0 space-y-3">
          <p className="text-sm font-semibold text-primary">{copy.brand}</p>
          <div className="space-y-2">
            <h1 className="break-words text-3xl font-extrabold tracking-tight md:text-4xl">{dashboardCopy.title}</h1>
            <p className="max-w-3xl break-words text-muted-foreground">
              {formatCopy(dashboardCopy.description, { name: displayName })}
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm">
          <span className="block font-semibold text-foreground">{dashboardCopy.todayDate}</span>
          <time className="text-muted-foreground" dateTime={todayKey}>
            {formattedToday}
          </time>
        </div>
      </header>

      {dashboardData.error && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <h2 className="font-bold">{dashboardCopy.dataErrorTitle}</h2>
          <p className="mt-1">{dashboardData.error}</p>
        </section>
      )}

      <div className={`grid min-w-0 gap-4 ${showWeightFluidCard ? "xl:grid-cols-5" : "lg:grid-cols-4"}`}>
        {nutrientCards.map((card) => (
          <NutrientCard key={card.key} card={card} copy={dashboardCopy} />
        ))}
        {showWeightFluidCard && <WeightFluidCard copy={dashboardCopy} settings={settings} />}
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <SafetySummary copy={dashboardCopy} summary={safetySummary} cards={nutrientCards} />
        <QuickActions copy={dashboardCopy} />
      </div>

      <section className="rounded-lg border border-border bg-card p-5">
        <div className="mb-4 flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <CalendarDays className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="break-words text-lg font-bold">{dashboardCopy.dailyProgressTitle}</h2>
            <p className="break-words text-sm text-muted-foreground">{dashboardCopy.dailyProgressDescription}</p>
          </div>
        </div>
        <div className="grid min-w-0 gap-3 md:grid-cols-3">
          {periodStats.map((period) => (
            <PeriodCard key={period.key} copy={dashboardCopy} period={period} />
          ))}
        </div>
      </section>

      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]">
        <ApprovedTips copy={dashboardCopy} lang={lang} tips={tips} />
        <QuoteCard copy={dashboardCopy} lang={lang} quote={quote} />
      </div>

      <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        {dashboardCopy.footerSafety}
      </p>
    </section>
  );
}

function NutrientCard({ card, copy }) {
  const Icon = nutrientIcons[card.key] || Apple;
  const styles = statusStyles[card.status] || statusStyles[V2_DASHBOARD_STATUS.safe];
  const progressLabel = `${card.label}: ${card.percentage}% ${copy.percentageUsed}`;

  return (
    <article className="min-w-0 rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="break-words text-sm font-semibold text-muted-foreground">{card.label}</p>
          <p className="break-words text-2xl font-extrabold">
            {formatAmount(card.consumed)} <span className="text-sm font-semibold text-muted-foreground">{card.unit}</span>
          </p>
          <p className="sr-only">{copy.consumed}</p>
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${styles.icon}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>

      <div className="mt-4 space-y-3">
        <div
          className="h-2 rounded-full bg-muted"
          role="progressbar"
          aria-label={progressLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={card.progress}
          aria-valuetext={progressLabel}
        >
          <div className={`h-2 rounded-full ${styles.bar}`} style={{ width: `${card.progress}%` }} />
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            {card.percentage}% {copy.percentageUsed}
          </span>
          <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${styles.badge}`}>{card.statusLabel}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-border pt-3 text-sm">
          <MetricRow label={copy.limit} value={card.targetConfigured ? `${formatAmount(card.limit)} ${card.unit}` : copy.notSet} />
          <MetricRow label={copy.remaining} value={card.targetConfigured ? `${formatAmount(card.remaining)} ${card.unit}` : copy.notSet} />
        </div>
      </div>
    </article>
  );
}

function WeightFluidCard({ copy, settings }) {
  const weight = settings.profile?.weight_kg || settings.profile?.weight;
  const fluidLimit = settings.daily_targets?.fluid_limit_ml;
  const targetWeight = settings.daily_targets?.weight_target;

  return (
    <article className="min-w-0 rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words text-sm font-semibold text-muted-foreground">{copy.weightFluid}</p>
          <p className="mt-1 break-words text-2xl font-extrabold">{weight ? `${formatAmount(weight)} kg` : copy.notSet}</p>
        </div>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-200">
          <Scale className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
      <div className="mt-4 space-y-3 border-t border-border pt-3 text-sm">
        <MetricRow label={copy.currentWeight} value={weight ? `${formatAmount(weight)} kg` : copy.notSet} />
        <MetricRow label={copy.targetWeight} value={targetWeight ? `${formatAmount(targetWeight)} kg` : copy.notSet} />
        <MetricRow label={copy.fluidLimit} value={fluidLimit ? `${formatAmount(fluidLimit)} ml` : copy.notSet} />
      </div>
    </article>
  );
}

function SafetySummary({ copy, summary, cards }) {
  const styles = statusStyles[summary.tone] || statusStyles[V2_DASHBOARD_STATUS.safe];

  return (
    <section className="min-w-0 rounded-lg border border-border bg-card p-5">
      <div className="flex min-w-0 items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${styles.icon}`}>
          <Sparkles className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-1">
          <h2 className="break-words text-lg font-bold">{copy.safetySummaryTitle}</h2>
          <p className="break-words text-sm text-muted-foreground">{copy.safetySummaryDescription}</p>
        </div>
      </div>
      <div className="mt-5 rounded-lg border border-border bg-background p-4">
        <p className="break-words text-base font-bold">{summary.title}</p>
        <p className="mt-1 break-words text-sm text-muted-foreground">{summary.detail}</p>
      </div>
      <div className="mt-4 grid min-w-0 gap-2 sm:grid-cols-2">
        {cards.map((card) => (
          <div key={card.key} className="flex min-w-0 items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2 text-sm">
            <span className="min-w-0 break-words font-medium">{card.label}</span>
            <span className="shrink-0 text-muted-foreground">{card.statusLabel}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function QuickActions({ copy }) {
  const quickActions = [
    { label: copy.addFood, to: "/food-logging", icon: ClipboardPlus },
    { label: copy.addCustomFood, to: "/foods/add", icon: Plus },
    { label: copy.openMixer, to: "/mixer", icon: SlidersHorizontal },
    { label: copy.viewHistory, to: "/history", icon: History },
    { label: copy.reminders, to: "/reminders", icon: Bell },
    { label: copy.settings, to: "/settings", icon: Settings },
  ];

  return (
    <section className="min-w-0 rounded-lg border border-border bg-card p-5">
      <h2 className="break-words text-lg font-bold">{copy.quickActionsTitle}</h2>
      <div className="mt-4 grid min-w-0 gap-2 sm:grid-cols-2">
        {quickActions.map((action) => {
          const Icon = action.icon;

          return (
            <Button key={action.to} asChild variant="outline" className="min-h-11 justify-between gap-3">
              <Link to={action.to} aria-label={action.label}>
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{action.label}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
              </Link>
            </Button>
          );
        })}
      </div>
    </section>
  );
}

function PeriodCard({ copy, period }) {
  return (
    <article className="min-w-0 rounded-lg border border-border bg-background p-4">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="break-words font-bold">{period.label}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {period.logCount} {copy.entries}
          </p>
        </div>
        <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-bold text-muted-foreground">
          {period.hasConfiguredTargets ? `${period.averageLimitUse}% ${copy.averageLimitUse}` : copy.noTargets}
        </span>
      </div>
      {period.hasLogs ? (
        <div className="mt-3">
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{copy.totalNutrients}</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <MetricRow label={copy.nutrients.potassium} value={`${formatAmount(period.totals.potassium)} mg`} />
            <MetricRow label={copy.nutrients.phosphorus} value={`${formatAmount(period.totals.phosphorus)} mg`} />
            <MetricRow label={copy.nutrients.sodium} value={`${formatAmount(period.totals.sodium)} mg`} />
            <MetricRow label={copy.nutrients.protein} value={`${formatAmount(period.totals.protein)} g`} />
          </div>
        </div>
      ) : (
        <p className="mt-3 rounded-lg bg-muted p-3 text-sm text-muted-foreground">{copy.noPeriodLogs}</p>
      )}
    </article>
  );
}

function ApprovedTips({ copy, lang, tips }) {
  const visibleTips = Array.isArray(tips) ? tips : [];

  return (
    <section className="min-w-0 rounded-lg border border-border bg-card p-5">
      <h2 className="break-words text-lg font-bold">{copy.approvedTips}</h2>
      <div className="mt-4 space-y-3">
        {visibleTips.length > 0 ? (
          visibleTips.map((tip) => {
            const title = getLocalizedTipField(tip, "title", lang);
            const body = getLocalizedTipField(tip, "body", lang);
            const category = lang === "ar" ? tip.category_ar || tip.category : tip.category;

            return (
              <article key={tip.id} className="min-w-0 rounded-lg border border-border bg-background p-4">
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <h3 className="min-w-0 break-words font-bold">{title}</h3>
                  {category && (
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                      {category}
                    </span>
                  )}
                </div>
                {body ? <p className="mt-2 break-words text-sm text-muted-foreground">{body}</p> : null}
              </article>
            );
          })
        ) : (
          <p className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">{copy.noApprovedTips}</p>
        )}
      </div>
    </section>
  );
}

function QuoteCard({ copy, lang, quote }) {
  const quoteText = lang === "ar" ? quote?.quote_ar || quote?.quote_en || quote?.quote : quote?.quote_en || quote?.quote;

  return (
    <section className="min-w-0 rounded-lg border border-border bg-card p-5">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Quote className="h-5 w-5" aria-hidden="true" />
        </span>
        <h2 className="break-words text-lg font-bold">{copy.quoteOfTheDay}</h2>
      </div>
      {quoteText ? (
        <blockquote className="mt-5 break-words rounded-lg border border-border bg-background p-5 text-xl font-extrabold leading-snug">
          "{quoteText}"
        </blockquote>
      ) : (
        <p className="mt-4 rounded-lg bg-muted p-3 text-sm text-muted-foreground">{copy.noQuote}</p>
      )}
    </section>
  );
}

function MetricRow({ label, value }) {
  return (
    <div className="min-w-0">
      <span className="block break-words text-xs font-medium text-muted-foreground">{label}</span>
      <span className="break-words font-bold">{value}</span>
    </div>
  );
}

function getLocalizedTipField(tip, field, lang) {
  if (lang === "ar") return tip?.[`${field}_ar`] || tip?.[`${field}_en`] || tip?.[field] || "";
  return tip?.[`${field}_en`] || tip?.[field] || "";
}

function formatCopy(template = "", values = {}) {
  return Object.entries(values).reduce((result, [key, value]) => result.replace(`{${key}}`, value), template);
}

function formatDateForDisplay(dateKey, lang) {
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey;

  return parsed.toLocaleDateString(lang === "ar" ? "ar" : undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
